import clsx from 'clsx';
import RiskBadge from './RiskBadge';

interface GeographicTractGroup {
  label: string;
  tractCount: number;
  applications: number;
  originations: number;
  denials: number;
  denialRate: number;
  penetrationRate: number;
}

interface GeographicResult {
  totalTracts: number;
  totalApplications: number;
  tractGroups: GeographicTractGroup[];
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  notes: string[];
}

export default function GeographicTable({ result }: { result: GeographicResult }) {
  if (!result || result.tractGroups.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        <p>No geographic data available.</p>
        {result?.notes?.map((n, i) => (
          <p key={i} className="mt-2 text-xs text-gray-400">{n}</p>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-600">
          <strong>{result.totalTracts}</strong> census tracts analyzed
        </span>
        <span className="text-gray-600">
          <strong>{result.totalApplications.toLocaleString()}</strong> total applications
        </span>
        <RiskBadge level={result.riskLevel} />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b">
              <th className="py-2 pr-4">Tract Group</th>
              <th className="py-2 pr-4">Tracts</th>
              <th className="py-2 pr-4">Applications</th>
              <th className="py-2 pr-4">Originations</th>
              <th className="py-2 pr-4">Denials</th>
              <th className="py-2 pr-4">Denial Rate</th>
              <th className="py-2 pr-4">Apps/Tract</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {result.tractGroups.map((g, i) => (
              <tr
                key={i}
                className={clsx(
                  i === result.tractGroups.length - 1 && result.riskLevel !== 'LOW' && 'bg-red-50',
                )}
              >
                <td className="py-2 pr-4 font-medium text-gray-900">{g.label}</td>
                <td className="py-2 pr-4 text-gray-600">{g.tractCount}</td>
                <td className="py-2 pr-4 text-gray-600">{g.applications.toLocaleString()}</td>
                <td className="py-2 pr-4 text-gray-600">{g.originations.toLocaleString()}</td>
                <td className="py-2 pr-4 text-gray-600">{g.denials.toLocaleString()}</td>
                <td className="py-2 pr-4 font-semibold">{(g.denialRate * 100).toFixed(1)}%</td>
                <td className="py-2 pr-4 text-gray-600">{g.penetrationRate.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Notes */}
      {result.notes.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-xs font-medium text-blue-800 mb-1">Notes & Limitations</p>
          {result.notes.map((n, i) => (
            <p key={i} className="text-xs text-blue-700">{n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
